import { ref, computed } from 'vue'

// Types
export interface Product {
  id: number
  name: string
  description: string
  price: number
  rating: number
  image: string
  category: string
  stock: number
  sku: string
}

export interface Course {
  id: number
  title: string
  description: string
  price: number
  duration: string
  level: string
  category: string
  image: string
  instructor: string
  modules: number
  certificate: boolean
}

export interface Certificate {
  id: number
  type: string
  status: 'Active' | 'Pending' | 'Expired' | 'Rejected'
  expirationDate: string
  uploadDate: string
  image: string
  verifiedBy?: string
  verifiedDate?: string
}

export interface CartItem {
  id: number
  type: 'product' | 'course'
  name: string
  price: number
  quantity: number
  image: string
}

export interface Order {
  id: string
  items: CartItem[]
  total: number
  status: 'pending' | 'processing' | 'completed' | 'cancelled'
  paymentMethod: string
  orderDate: string
  shippingAddress?: string
}

// Store Service
class StoreService {
  private cart = ref<CartItem[]>([])
  private orders = ref<Order[]>([])
  private certificates = ref<Certificate[]>([])
  private enrolledCourses = ref<Course[]>([])

  // Cart Management
  addToCart(item: Product | Course, type: 'product' | 'course') {
    const existingItem = this.cart.value.find(cartItem => cartItem.id === item.id && cartItem.type === type)
    
    if (existingItem) {
      existingItem.quantity += 1
    } else {
      this.cart.value.push({
        id: item.id,
        type,
        name: 'name' in item ? item.name : item.title,
        price: item.price,
        quantity: 1,
        image: item.image
      })
    }
    
    this.saveCartToStorage()
    this.showNotification(`${'name' in item ? item.name : item.title} added to cart`)
  }

  removeFromCart(itemId: number, type: 'product' | 'course') {
    this.cart.value = this.cart.value.filter(item => !(item.id === itemId && item.type === type))
    this.saveCartToStorage()
  }

  updateQuantity(itemId: number, type: 'product' | 'course', quantity: number) {
    const item = this.cart.value.find(cartItem => cartItem.id === itemId && cartItem.type === type)
    if (item) {
      if (quantity <= 0) {
        this.removeFromCart(itemId, type)
      } else {
        item.quantity = quantity
        this.saveCartToStorage()
      }
    }
  }

  clearCart() {
    this.cart.value = []
    this.saveCartToStorage()
  }

  // Computed properties
  getCartTotal() {
    return computed(() => {
      return this.cart.value.reduce((total, item) => total + (item.price * item.quantity), 0)
    })
  }

  getCartItemCount() {
    return computed(() => {
      return this.cart.value.reduce((count, item) => count + item.quantity, 0)
    })
  }

  // Storage
  saveCartToStorage() {
    localStorage.setItem('storeCart', JSON.stringify(this.cart.value))
  }

  loadCartFromStorage() {
    const savedCart = localStorage.getItem('storeCart')
    if (savedCart) {
      this.cart.value = JSON.parse(savedCart)
    }
  }

  // Payment Processing
  async processPayment(paymentMethod: string, shippingAddress?: string): Promise<Order> {
    const order: Order = {
      id: this.generateOrderId(),
      items: [...this.cart.value],
      total: this.getCartTotal().value,
      status: 'processing',
      paymentMethod,
      orderDate: new Date().toISOString(),
      shippingAddress
    }

    // Simulate payment processing
    await new Promise(resolve => setTimeout(resolve, 2000))

    // Add to orders
    this.orders.value.push(order)
    this.saveOrdersToStorage()

    // Clear cart
    this.clearCart()

    // Process enrolled courses
    const courseItems = order.items.filter(item => item.type === 'course')
    for (const item of courseItems) {
      await this.enrollInCourse(item.id)
    }

    return order
  }

  // Course Enrollment
  async enrollInCourse(courseId: number) {
    // Find course in available courses
    const course = this.getAvailableCourses().find(c => c.id === courseId)
    if (course && !this.enrolledCourses.value.find(ec => ec.id === courseId)) {
      this.enrolledCourses.value.push(course)
      this.saveEnrolledCoursesToStorage()
      this.showNotification(`Successfully enrolled in ${course.title}`)
    }
  }

  // Certificate Management
  async uploadCertificate(certificateData: {
    type: string
    expirationDate: string
    image: File
  }): Promise<Certificate> {
    // Simulate file upload
    await new Promise(resolve => setTimeout(resolve, 1500))

    const certificate: Certificate = {
      id: Date.now(),
      type: certificateData.type,
      status: 'Pending',
      expirationDate: certificateData.expirationDate,
      uploadDate: new Date().toISOString(),
      image: URL.createObjectURL(certificateData.image)
    }

    this.certificates.value.push(certificate)
    this.saveCertificatesToStorage()
    this.showNotification('Certificate uploaded successfully')

    return certificate
  }

  async verifyCertificate(certificateId: number, verifiedBy: string) {
    const certificate = this.certificates.value.find(c => c.id === certificateId)
    if (certificate) {
      certificate.status = 'Active'
      certificate.verifiedBy = verifiedBy
      certificate.verifiedDate = new Date().toISOString()
      this.saveCertificatesToStorage()
    }
  }

  // Data Management
  getAvailableProducts(): Product[] {
    return []
  }

  getAvailableCourses(): Course[] {
    return []
  }

  // Storage methods
  saveOrdersToStorage() {
    localStorage.setItem('storeOrders', JSON.stringify(this.orders.value))
  }

  loadOrdersFromStorage() {
    const savedOrders = localStorage.getItem('storeOrders')
    if (savedOrders) {
      this.orders.value = JSON.parse(savedOrders)
    }
  }

  saveEnrolledCoursesToStorage() {
    localStorage.setItem('enrolledCourses', JSON.stringify(this.enrolledCourses.value))
  }

  loadEnrolledCoursesFromStorage() {
    const savedCourses = localStorage.getItem('enrolledCourses')
    if (savedCourses) {
      this.enrolledCourses.value = JSON.parse(savedCourses)
    }
  }

  saveCertificatesToStorage() {
    localStorage.setItem('certificates', JSON.stringify(this.certificates.value))
  }

  loadCertificatesFromStorage() {
    const savedCertificates = localStorage.getItem('certificates')
    if (savedCertificates) {
      this.certificates.value = JSON.parse(savedCertificates)
    }
  }

  // Utility methods
  generateOrderId(): string {
    return `ORD-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`
  }

  showNotification(message: string) {
    // Create notification element
    const notification = document.createElement('div')
    notification.className = 'fixed top-4 right-4 bg-emerald-500 text-white px-6 py-3 rounded-lg shadow-lg z-50 transform translate-x-full transition-transform duration-300'
    notification.textContent = message
    
    document.body.appendChild(notification)
    
    // Animate in
    setTimeout(() => {
      notification.classList.remove('translate-x-full')
    }, 100)
    
    // Remove after 3 seconds
    setTimeout(() => {
      notification.classList.add('translate-x-full')
      setTimeout(() => {
        document.body.removeChild(notification)
      }, 300)
    }, 3000)
  }

  // Getters
  getCart() {
    return this.cart
  }

  getOrders() {
    return this.orders
  }

  getCertificates() {
    return this.certificates
  }

  getEnrolledCourses() {
    return this.enrolledCourses
  }
}

// Export singleton instance
export const storeService = new StoreService()
